import { STOREFRONTS } from './storefronts.js';

const HISTORY_KEY = 'region_selector_history';

const flagFromCode = (code) => {
  if (!code) return '🌐';
  const up = code.toUpperCase();
  return up
    .slice(0, 2)
    .split('')
    .map((ch) => String.fromCodePoint(127397 + ch.charCodeAt(0)))
    .join('');
};

const loadHistory = () => {
  try {
    const raw = localStorage.getItem(HISTORY_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch (_) {
    return [];
  }
};

const saveHistory = (codes) => {
  try {
    localStorage.setItem(HISTORY_KEY, JSON.stringify(codes.slice(0, 6)));
  } catch (_) {
    /* ignore */
  }
};

async function fetchStorefronts() {
  try {
    const resp = await fetch('/api/storefronts');
    if (!resp.ok) throw new Error('failed');
    const data = await resp.json();
    return data;
  } catch (_) {
    return {
      hot: STOREFRONTS.slice(0, 6),
      groups: [{ id: 'all', label: '全部', items: STOREFRONTS }],
      all: STOREFRONTS,
    };
  }
}

function positionDropdown(trigger, dropdown) {
  const rect = trigger.getBoundingClientRect();
  dropdown.style.position = 'fixed';
  dropdown.style.top = `${rect.bottom + 6}px`;
  dropdown.style.left = `${rect.left}px`;
  dropdown.style.minWidth = `${rect.width}px`;
  dropdown.style.zIndex = '9999';
}

export function createRegionSelector(targetId, options = {}) {
  const target = document.getElementById(targetId);
  if (!target) return null;
  const { defaultCode = 'us', onChange, searchable = true } = options;

  target.classList.add('region-selector');
  target.innerHTML = `
    <div class="rs-trigger" tabindex="0">
      <span class="flag">🌐</span>
      <span class="chevron">▼</span>
    </div>
    <div class="rs-dropdown hidden">
      <div class="rs-search">
        <input type="text" placeholder="搜索地区/国家">
      </div>
      <div class="rs-list"></div>
    </div>
  `;

  const trigger = target.querySelector('.rs-trigger');
  const dropdown = target.querySelector('.rs-dropdown');
  const listEl = target.querySelector('.rs-list');
  const searchInput = target.querySelector('.rs-search input');

  // Move dropdown to body to avoid clipping inside overflowing containers
  document.body.appendChild(dropdown);

  let currentCode = defaultCode;
  let dataset = { hot: [], groups: [], all: [] };
  let history = loadHistory();
  const reposition = () => {
    if (!dropdown.classList.contains('hidden')) {
      positionDropdown(trigger, dropdown);
    }
  };
  window.addEventListener('scroll', reposition, true);
  window.addEventListener('resize', reposition);

  const formatItem = (item) => {
    if (!item) return null;
    const flag = item.flag || flagFromCode(item.code);
    const name = item.name || item.code.toUpperCase();
    return `
      <div class="rs-item" data-code="${item.code}">
        <span class="flag">${flag}</span>
        <span class="label">${name}</span>
        <span class="code-text">${item.code.toUpperCase()}</span>
      </div>
    `;
  };

  const renderSection = (title, items = []) => {
    if (!items.length) return '';
    const htmlItems = items.map((i) => formatItem(i)).join('');
    return `
      <div class="rs-section">
        <div class="rs-section-title">${title}</div>
        ${htmlItems}
      </div>
    `;
  };

  const renderList = (filter = '') => {
    const f = filter.trim().toLowerCase();
    if (f) {
      const matched = dataset.all.filter(
        (s) =>
          s.code.toLowerCase().includes(f) ||
          (s.name && s.name.toLowerCase().includes(f)),
      );
      listEl.innerHTML = matched.length
        ? renderSection('搜索结果', matched)
        : `<div class="rs-empty">无匹配地区</div>`;
      bindItemClicks();
      return;
    }

    const seen = new Set();
    const histItems = history
      .map((code) => dataset.all.find((i) => i.code === code))
      .filter(Boolean);
    histItems.forEach((i) => seen.add(i.code));

    const hotItems = (dataset.hot || []).filter((i) => {
      if (seen.has(i.code)) return false;
      seen.add(i.code);
      return true;
    });

    const groupHtml = (dataset.groups || [])
      .map((g) => renderSection(g.label, (g.items || []).filter(Boolean)))
      .join('');

    listEl.innerHTML = `
      ${renderSection('查询历史', histItems)}
      ${renderSection('热门地区', hotItems)}
      ${groupHtml || '<div class="rs-empty">无可用地区</div>'}
    `;
    bindItemClicks();
  };

  const setCode = (code) => {
    currentCode = code;
    const item =
      (dataset.all || []).find((s) => s.code === code) ||
      STOREFRONTS.find((s) => s.code === code);
    trigger.querySelector('.flag').textContent = item?.flag || flagFromCode(code);

    history = [code, ...history.filter((c) => c !== code)];
    saveHistory(history);

    if (onChange) onChange(code);
  };

  const show = () => {
    positionDropdown(trigger, dropdown);
    dropdown.classList.remove('hidden');
  };
  const hide = () => dropdown.classList.add('hidden');

  const bindItemClicks = () => {
    dropdown.querySelectorAll('.rs-item').forEach((el) => {
      el.addEventListener('click', () => {
        const code = el.dataset.code;
        setCode(code);
        hide();
      });
    });
  };

  trigger.addEventListener('click', () => {
    const isHidden = dropdown.classList.contains('hidden');
    document.querySelectorAll('.rs-dropdown').forEach((d) => d.classList.add('hidden'));
    if (isHidden) show();
  });

  document.addEventListener('click', (e) => {
    if (!target.contains(e.target) && !dropdown.contains(e.target)) hide();
  });

  searchInput.addEventListener('input', (e) => {
    if (!searchable) return;
    renderList(e.target.value);
  });

  fetchStorefronts().then((data) => {
    dataset = data || dataset;
    renderList();
    setCode(defaultCode);
  });

  return {
    getCode: () => currentCode,
    setCode,
  };
}

// 多选版本（简化 UI，为关键词页使用）
export function createRegionMultiSelector(targetId, options = {}) {
  const target = document.getElementById(targetId);
  if (!target) return null;
  const {
    defaultCodes = [],
    onChange,
    onConfirm,
    searchable = true,
    placeholder = '选择国家/地区',
    confirmButtonText = '确认',
    data = STOREFRONTS,
  } = options;

  const hasConfirmBtn = typeof onConfirm === 'function';

  target.innerHTML = `
    <div class="rs-trigger" tabindex="0">
      <span class="flag">🌐</span>
      <span class="label">${placeholder}</span>
      <span class="code-text"></span>
      <span class="chevron">▼</span>
    </div>
    <div class="rs-dropdown hidden">
      <div class="rs-search-row">
        <div class="rs-search">
          <input type="text" placeholder="搜索地区/国家">
        </div>
        ${hasConfirmBtn ? `<button class="rs-confirm-btn" type="button">${confirmButtonText}</button>` : ''}
      </div>
      <div class="rs-list"></div>
      <div class="rs-empty hidden">无匹配地区</div>
    </div>
  `;

  const trigger = target.querySelector('.rs-trigger');
  const dropdown = target.querySelector('.rs-dropdown');
  const listEl = target.querySelector('.rs-list');
  const searchInput = target.querySelector('.rs-search input');

  // Move dropdown to body to avoid clipping and keep positioning stable
  document.body.appendChild(dropdown);

  let codes = [...defaultCodes];
  let dataset = data || [];
  const reposition = () => {
    if (!dropdown.classList.contains('hidden')) {
      positionDropdown(trigger, dropdown);
    }
  };
  window.addEventListener('scroll', reposition, true);
  window.addEventListener('resize', reposition);

  const renderList = (filter = '') => {
    const f = filter.trim().toLowerCase();
    const filtered = dataset.filter(
      (s) =>
        s.code.toLowerCase().includes(f) ||
        (s.name && s.name.toLowerCase().includes(f)) ||
        (s.flag && s.flag.includes(filter)),
    );
    listEl.innerHTML = filtered
      .map(
        (s) => `
        <div class="rs-item" data-code="${s.code}">
          <span class="flag">${s.flag || flagFromCode(s.code)}</span>
          <span class="label">${s.name || s.code.toUpperCase()}</span>
          <span class="code-text">${s.code.toUpperCase()}</span>
          <input type="checkbox" ${codes.includes(s.code) ? 'checked' : ''} />
        </div>
      `,
      )
      .join('');
    const emptyEl = dropdown.querySelector('.rs-empty');
    if (emptyEl) emptyEl.classList.toggle('hidden', filtered.length > 0);
    bindItemClicks();
    updateTrigger();
  };

  const updateTrigger = () => {
    const labelEl = trigger.querySelector('.label');
    const codeEl = trigger.querySelector('.code-text');
    if (!codes.length) {
      labelEl.textContent = placeholder;
      codeEl.textContent = '';
    } else {
      const names = codes
        .map((c) => dataset.find((i) => i.code === c)?.name || c.toUpperCase())
        .slice(0, 2)
        .join(', ');
      labelEl.textContent = names + (codes.length > 2 ? ` +${codes.length - 2}` : '');
      codeEl.textContent = '';
    }
    const first = dataset.find((i) => i.code === codes[0]);
    trigger.querySelector('.flag').textContent = first?.flag || '🌐';
  };

  const bindItemClicks = () => {
    dropdown.querySelectorAll('.rs-item').forEach((el) => {
      el.addEventListener('click', (e) => {
        if (e.target.tagName.toLowerCase() === 'input') return;
        const code = el.dataset.code;
        const checked = !codes.includes(code);
        if (checked) codes.push(code);
        else codes = codes.filter((c) => c !== code);
        renderList(searchInput.value);
        if (onChange) onChange(codes);
      });
      const cb = el.querySelector('input[type="checkbox"]');
      cb.addEventListener('change', (e) => {
        const code = el.dataset.code;
        if (e.target.checked) {
          if (!codes.includes(code)) codes.push(code);
        } else {
          codes = codes.filter((c) => c !== code);
        }
        if (onChange) onChange(codes);
        updateTrigger();
      });
    });
  };

  const show = () => {
    positionDropdown(trigger, dropdown);
    dropdown.classList.remove('hidden');
  };
  const hide = () => dropdown.classList.add('hidden');

  trigger.addEventListener('click', () => {
    const isHidden = dropdown.classList.contains('hidden');
    document.querySelectorAll('.rs-dropdown').forEach((d) => d.classList.add('hidden'));
    if (isHidden) show();
  });

  document.addEventListener('click', (e) => {
    if (!target.contains(e.target) && !dropdown.contains(e.target)) hide();
  });

  searchInput.addEventListener('input', (e) => {
    if (!searchable) return;
    renderList(e.target.value);
  });

  // 确认按钮点击事件
  const confirmBtn = dropdown.querySelector('.rs-confirm-btn');
  if (confirmBtn && onConfirm) {
    confirmBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      onConfirm(codes);
    });
  }

  // 更新确认按钮状态
  const updateConfirmButton = (text, visible = true, disabled = false) => {
    if (!confirmBtn) return;
    if (text) confirmBtn.textContent = text;
    confirmBtn.style.display = visible ? '' : 'none';
    confirmBtn.disabled = disabled;
  };

  renderList();
  updateTrigger();

  return {
    getCodes: () => codes,
    setCodes: (list) => {
      codes = Array.from(new Set(list || []));
      updateTrigger();
      renderList(searchInput.value);
    },
    updateConfirmButton,
    hideDropdown: hide,
  };
}
